import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Switch } from "../../ui/switch";
import { Card, inr, packPrice, unitPrice } from "./PricingUi";

const input = "h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:border-sky-400 focus:bg-white";

const PackRow = ({ pack, i, unit, base, onChange, onRemove, canRemove }) => {
  const price = packPrice(pack.credits, unit);
  const cost = Number(pack.credits || 0) * Number(base || 0);
  return (
    <div className="rounded-2xl border border-slate-200 p-4 space-y-3" data-testid={`pricing-pack-${i}`}>
      <div className="grid grid-cols-2 sm:grid-cols-[1.2fr_0.8fr_2fr] gap-3">
        <input value={pack.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="Pack name" maxLength={40} className={input} data-testid={`pricing-pack-${i}-name`} />
        <input type="number" min={1} value={pack.credits} onChange={(e) => onChange({ credits: e.target.value === "" ? "" : Number(e.target.value) })}
          placeholder="Credits" className={`${input} font-mono`} data-testid={`pricing-pack-${i}-credits`} />
        <input value={pack.tagline} onChange={(e) => onChange({ tagline: e.target.value })} placeholder="Short line, e.g. Ship a real project" maxLength={80}
          className={`${input} col-span-2 sm:col-span-1`} data-testid={`pricing-pack-${i}-tagline`} />
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        <span className="font-display text-lg font-bold text-slate-900" data-testid={`pricing-pack-${i}-price`}>{inr(price)}</span>
        <span className="text-slate-500">cost {inr(cost)}</span>
        <span className={`font-medium ${price - cost < 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid={`pricing-pack-${i}-profit`}>profit {inr(price - cost)}</span>
        <label className="flex items-center gap-2 text-slate-600 ml-auto"><Switch checked={!!pack.popular} onCheckedChange={(v) => onChange({ popular: v })} data-testid={`pricing-pack-${i}-popular`} className="data-[state=checked]:bg-sky-500" /> Most popular</label>
        <label className="flex items-center gap-2 text-slate-600"><Switch checked={pack.active !== false} onCheckedChange={(v) => onChange({ active: v })} data-testid={`pricing-pack-${i}-active`} className="data-[state=checked]:bg-emerald-500" /> On sale</label>
        <button onClick={onRemove} disabled={!canRemove} aria-label="Remove pack" data-testid={`pricing-pack-${i}-remove`} className="text-slate-400 hover:text-rose-600 disabled:opacity-30"><Trash2 size={16} /></button>
      </div>
    </div>
  );
};

export const PacksCard = ({ p, set }) => {
  const unit = unitPrice(p.base_cost_inr, p.markup_pct);
  const update = (i, upd) => set({ packs: p.packs.map((x, j) => (j === i ? { ...x, ...upd } : x)) });
  const add = () => set({ packs: [...p.packs, { name: "New pack", credits: 100, tagline: "", popular: false, active: true }] });
  return (
    <Card title="Credit packs" testid="pricing-packs-card" desc="What users can buy on the Billing page. Prices update from your cost and markup."
      right={p.packs.length < 8 && (
        <button onClick={add} data-testid="pricing-add-pack-btn" className="h-9 px-4 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-medium text-slate-800 flex items-center gap-1.5"><Plus size={15} /> Add pack</button>
      )}>
      <div className="space-y-3">
        {p.packs.map((pk, i) => (
          <PackRow key={pk.id || `new-${i}`} pack={pk} i={i} unit={unit} base={p.base_cost_inr} canRemove={p.packs.length > 1}
            onChange={(u) => update(i, u)} onRemove={() => set({ packs: p.packs.filter((_, j) => j !== i) })} />
        ))}
      </div>
    </Card>
  );
};

export default PacksCard;
